/**
 * ItemModal Component - Complete modifier selection and pizza builder
 * 
 * =============================================================================
 * MODIFIER SYSTEM DOCUMENTATION
 * =============================================================================
 * 
 * GROUP-LEVEL FIELDS:
 * -------------------
 * - name: Display name for the group (e.g., "Choose your size", "Add toppings")
 * - optionType: Selection behavior
 *     0 = Select (dropdown/single selection)
 *     1 = Multiple (checkboxes, can select many)
 *     2 = Single (radio buttons, exactly one)
 * - required: 0 or 1 - whether selection is mandatory
 * - eligibleQuantityMin: Minimum selections required (overrides required if > 0)
 * - eligibleQuantityMax: Maximum selections allowed (0 = unlimited)
 * - multiSelect: Boolean, alternative to optionType for multi-select
 * - includedQty: Number of selections included free (first N free, then charge)
 * - subLimit: Maximum substitutions allowed
 * - groupType: Special handling - "toppings" enables topping-specific logic
 * - toppingMatch: "less" means substitutions must be ≤ base grade
 * - displayOrder: Sort order for display
 * 
 * OPTION-LEVEL FIELDS:
 * --------------------
 * - id: Unique identifier for the option
 * - name: Display name (e.g., "Pepperoni", "Extra Cheese")
 * - price: Price adjustment (string or number, can be negative for discounts)
 * - status: 1 = active, 0 = inactive (hidden)
 * - default: Boolean - option is pre-selected when item is added
 * - selected: Boolean - current selection state (for tracking)
 * - defaultSelected: Boolean - should be selected by default (alias for default)
 * - matchType: Grade level for topping matching (0-5, used with toppingMatch)
 * - displayOrder: Sort order within the group
 * - activeOn: Array of size indices where option is available (pizza-specific)
 * - halfPrice: Price for half portion (pizza-specific)
 * - eligibleQuantityMax: Max quantity for this specific option
 * 
 * PIZZA-SPECIFIC STRUCTURE:
 * -------------------------
 * Pizza items have itemOptions_json with:
 * {
 *   "size": [
 *     { "sizeName": "10 inch", "priceBaseTotal": "12.99", "index": 0 },
 *     { "sizeName": "14 inch", "priceBaseTotal": "16.99", "index": 1 }
 *   ],
 *   "toppings": [
 *     {
 *       "name": "WHOLE",  // or "LEFT", "RIGHT"
 *       "options": [
 *         {
 *           "id": "topping_1",
 *           "name": "Pepperoni",
 *           "default": true,
 *           "activeOn": [
 *             { "index": 0, "price": "1.50", "halfPrice": "0.75" },
 *             { "index": 1, "price": "2.00", "halfPrice": "1.00" }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 * 
 * PIZZA TOPPING STATES:
 * ---------------------
 * - "default": Included with the item (no charge)
 * - "add": Customer added topping (charged at price)
 * - "no": Customer removed topping (no charge)
 * - "extra": Double portion (charged at price × 2)
 * 
 * PIZZA SIDES:
 * ------------
 * - "WHOLE": Topping covers entire pizza (full price)
 * - "LEFT": Topping on left half only (uses halfPrice)
 * - "RIGHT": Topping on right half only (uses halfPrice)
 * 
 * =============================================================================
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function toNum(v) {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  // Remove currency symbols and commas
  const cleaned = String(v).replace(/[$,€£¥]/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

function money(n) {
  return (Math.round(n * 100) / 100).toFixed(2);
}

/**
 * Compute line pricing for standard (entree) options, including the
 * percentage-surcharge option types:
 *   - optionType 5: surcharge = percent% of the MODIFIERS subtotal
 *   - optionType 6: surcharge = percent% of the WHOLE line (base + modifiers)
 * Normal options contribute their flat `price`. `percent` lives on picked
 * surcharge entries (sourced from the option's `price` field; 100 = double).
 *
 * Single source of truth for both the live modal total and the cart line so
 * they can never drift.
 *
 * @returns {{ modifiersTotal:number, surcharge:number, modifierTotal:number, lineTotal:number }}
 *   modifierTotal = modifiersTotal + surcharge (what the cart stores as the
 *   per-line modifier contribution).
 */
function computeLinePricing(basePrice, picked, qty) {
  const base = toNum(basePrice);
  const modifiersTotal = (picked || []).reduce((s, o) => s + toNum(o.price || 0), 0);
  const surcharge = (picked || []).reduce((s, o) => {
    const pct = toNum(o.percent) / 100;
    if (o.optionType === 5) return s + modifiersTotal * pct;
    if (o.optionType === 6) return s + (base + modifiersTotal) * pct;
    return s;
  }, 0);
  const modifierTotal = modifiersTotal + surcharge;
  const lineTotal = (base + modifierTotal) * toNum(qty || 1);
  return { modifiersTotal, surcharge, modifierTotal, lineTotal };
}

function parseMaybe(v) {
  if (v == null) return null;
  if (typeof v === 'string') {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
}

function uid() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

// ============================================================================
// ITEM TYPE DETECTION
// ============================================================================

/**
 * Detect if an item is a pizza (has size + toppings structure)
 */
function isPizzaItem(item) {
  const raw = parseMaybe(item?.itemOptions_json);
  if (!raw) return false;
  const obj = Array.isArray(raw) ? (raw[0] || {}) : raw;
  return !!(obj && (obj.size || obj.sizes) && (obj.toppings || obj.toppingGroups));
}

/**
 * Get pizza configuration from item
 */
function getPizzaConfig(item) {
  const raw = parseMaybe(item?.itemOptions_json);
  if (!raw) return null;
  const obj = Array.isArray(raw) ? (raw[0] || {}) : raw;
  return (obj && (obj.size || obj.toppings)) ? obj : null;
}

/**
 * Expand a single menuGroup record into one or more option sections.
 *
 * menuGroup entry shape (from getMenu response, keyed by `recId`):
 *   { recId, groupName, groupType, status, groupOptions }
 *
 * groupOptions shape depends on groupType:
 *   - 'modifierOptions'  -> groupOptions is a FLAT array of options
 *                           => collapse into ONE section named groupName
 *   - 'singleModifier' /
 *     'multiModifier'    -> groupOptions is ALREADY an array of sections
 *                           (each has its own name/options/required/optionType...)
 *                           => return them as-is (one reference can yield several sections)
 */
function resolveGroupSections(group) {
  if (!group) return [];

  let groupOptions = parseMaybe(group.groupOptions) ?? group.groupOptions ?? [];
  if (!Array.isArray(groupOptions)) groupOptions = [];

  const type = group.groupType;

  // FLAT options -> one section
  if (type === 'modifierOptions') {
    const opts = groupOptions.filter(o => o && o.status !== 0);
    if (!opts.length) return [];
    return [{
      id: group.recId,
      name: group.groupName || 'Options',
      options: opts,
      required: 0,
      optionType: 1,        // checkbox-style by default
      multiSelect: false,
      eligibleQuantityMin: 0,
      eligibleQuantityMax: 0,
      includedQty: 0,
      subLimit: 0,
      displayOrder: 0,
      _fromGroup: group.recId
    }];
  }

  // singleModifier / multiModifier -> groupOptions IS the list of sections
  return groupOptions
    .filter(sec => sec && sec.status !== 0 && Array.isArray(sec.options))
    .map(sec => ({
      id: sec.id,
      name: sec.name || group.groupName || 'Options',
      options: sec.options,
      required: toNum(sec.required) || 0,
      optionType: sec.optionType ?? 1,
      multiSelect: sec.multiSelect ?? (sec.optionType === 1),
      eligibleQuantityMin: toNum(sec.eligibleQuantityMin) || 0,
      eligibleQuantityMax: toNum(sec.eligibleQuantityMax) || 0,
      includedQty: toNum(sec.includedQty) || 0,
      subLimit: toNum(sec.subLimit) || 0,
      displayOrder: toNum(sec.displayOrder) || 0,
      styleOptions: sec.styleOptions,
      _fromGroup: group.recId
    }));
}

/**
 * Get standard modifier sections from an item.
 *
 * itemOptions_json is either:
 *   a) inline sections        [{ id, name, options:[...], required, optionType, ... }]
 *   b) reference(s) to a group [{ type: "reference", status: 1, dataSource: 167 }]
 *
 * For (b), the group is found in `modGroups` (the getMenu `menuGroup` array)
 * by matching `recId === dataSource`, then expanded by groupType. A single
 * reference can expand into MULTIPLE sections (e.g. a multiModifier group).
 *
 * @param {object} item       menu item containing itemOptions_json
 * @param {array}  modGroups  the getMenu `menuGroup` array (recId-keyed records)
 */
function getOptionSections(item, modGroups = []) {
  let raw = parseMaybe(item?.itemOptions_json) || [];

  // Ensure it's an array
  if (!Array.isArray(raw)) {
    if (raw.sections && Array.isArray(raw.sections)) {
      raw = raw.sections;
    } else if (raw.options && Array.isArray(raw.options)) {
      raw = [{ name: raw.name || 'Options', options: raw.options }];
    } else {
      raw = [];
    }
  }

  const groups = Array.isArray(modGroups) ? modGroups : [];
  const findGroup = (dataSource) => groups.find(g =>
    g && (g.recId === dataSource || String(g.recId) === String(dataSource))
  );

  // Process array - resolve references and keep inline groups
  const resolved = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;

    // Handle reference entries -> resolve from menuGroup by recId
    // (status on the reference itself is optional and not always present)
    if (entry.type === 'reference' && entry.dataSource != null) {
      const group = findGroup(entry.dataSource);
      if (group && group.status !== 0) {
        resolved.push(...resolveGroupSections(group));
      }
      // Unresolvable / inactive reference (e.g. deleted group): skip silently
      continue;
    }

    // Inline modifier group (already has its own options array)
    if (Array.isArray(entry.options)) {
      resolved.push(entry);
      continue;
    }

    // Anything else (e.g. a bare flat option, pizza config) is not a standard group
  }

  // Sort by displayOrder when present (inline groups may not define it)
  resolved.sort((a, b) => (toNum(a.displayOrder) || 0) - (toNum(b.displayOrder) || 0));

  return resolved;
}

// ============================================================================
// PIZZA PRICING HELPERS
// ============================================================================

/**
 * Calculate price for a pizza topping based on its state and size
 */
function pricePizzaTopping(topping, sizeIndex) {
  const entry = topping.activeOn?.find(a => a.index === sizeIndex);
  const priceWhole = toNum(entry?.price);
  const priceHalf = toNum(entry?.halfPrice ?? entry?.price);
  
  // Default or removed toppings are free
  if (topping.state === 'no' || topping.state === 'default') return 0;
  
  // Half portions use half price
  const base = topping.side === 'WHOLE' ? priceWhole : priceHalf;
  
  // Extra is double
  return topping.state === 'extra' ? base * 2 : base;
}

/**
 * Create an option object for order submission from pizza topping state
 */
function makePizzaOption(topping, sizeIndex) {
  if (topping.state === 'no') {
    return { 
      id: String(topping.id), 
      sku: String(topping.id), 
      name: `no ${topping.name}`, 
      price: 0, 
      activeOn: topping.activeOn || [] 
    };
  }
  
  if (topping.state === 'default') {
    return null; // Don't include default toppings in the order
  }
  
  const labelSide = topping.side ? ` ${topping.side} ` : ' ';
  const label = `${labelSide}${topping.state === 'extra' ? 'EXTRA ' : ''}${topping.name}`;
  
  return { 
    id: String(topping.id), 
    sku: String(topping.id), 
    name: label.trim(), 
    price: pricePizzaTopping(topping, sizeIndex), 
    activeOn: topping.activeOn || [] 
  };
}

// ============================================================================
// STANDARD MODIFIER NORMALIZATION
// ============================================================================

/**
 * Normalize modifier sections into a consistent structure for rendering
 * 
 * optionType: 
 *   0 or undefined = Select (default, treat as checkbox)
 *   1 = Checkbox style (multiple selections allowed)
 *   2 = Radio button style (single selection only)
 * 
 * multiSelect:
 *   true = Show qty steppers (can add same option multiple times)
 *   false = Just on/off toggle
 * 
 * eligibleQuantityMax:
 *   0 = Unlimited selections
 *   N = Max N selections allowed
 */
function normalizeTree(sections) {
  const groups = [];
  const flat = {};
  const map = {};
  const idToGroup = {};

  const walk = (group, path) => {
    const key = path;
    const optionType = toNum(group.optionType || 0);
    
    // Radio = single select, Checkbox = multi select
    const isRadio = optionType === 2;
    
    // eligibleQuantityMax: 0 means unlimited
    const rawMax = toNum(group.eligibleQuantityMax);
    const max = rawMax === 0 ? 999 : rawMax;
    
    // For radio buttons, max is always 1
    const effectiveMax = isRadio ? 1 : max;
    
    const min = toNum(group.eligibleQuantityMin || (group.required ? 1 : 0));
    const included = toNum(group.includedQty || 0);
    const subLimit = toNum(group.subLimit || 0);
    const groupType = group.groupType || '';
    const toppingMatch = group.toppingMatch || '';
    
    // multiSelect = true means show qty steppers (can add same option multiple times)
    const showQtyStepper = !!group.multiSelect;
    
    const g = { 
      key, 
      name: group.name || group.title || 'Options', 
      min, 
      max: effectiveMax,
      included, 
      subLimit, 
      groupType, 
      toppingMatch, 
      isRadio,           // true = radio buttons, false = checkboxes
      showQtyStepper,    // true = show qty steppers for each option
      required: group.required || min > 0,
      optionType,
      options: [] 
    };

    (group.options || group.modifiers || []).forEach((opt, idx) => {
      const id = String(opt.id || opt.name || `${key}/opt${idx}`);
      const matchType = toNum(opt.matchType || 0);
      const isDefault = !!opt.default || !!opt.defaultSelected || !!opt.selected;
      const price = toNum(opt.price || 0);
      const optMax = toNum(opt.eligibleQuantityMax || 999);
      const status = opt.status !== undefined ? opt.status : 1;

      // Skip inactive options
      if (status === 0) return;

      const hasChildGroups = Array.isArray(opt.options) && opt.options.length > 0;

      flat[id] = {
        id,
        name: opt.name || opt.option_name,
        price,
        matchType,
        default: isDefault,
        optMax,
        isParent: hasChildGroups,
        showParent: opt.showParent
      };
      idToGroup[id] = key;

      const node = { 
        id, 
        name: opt.name || opt.option_name, 
        price, 
        matchType, 
        default: isDefault, 
        optMax,
        status,
        isParent: hasChildGroups,
        showParent: opt.showParent,
        // POS passthrough fields (kept lean — no catalog/nested data)
        sku: opt.sku || String(id),
        printerid: opt.printerid || '',
        kitchenPrintName: opt.KitchenPrintName || '',
        description: opt.description || '',
        displayOrder: toNum(opt.displayOrder) || 0
      };

      // Nested layers: an option's `options` array holds CHILD GROUPS (each with
      // its own optionType/required/options). Walk each as a full group so its
      // radio/checkbox/required semantics apply, and tag it with its parent so we
      // can hide it until the parent is selected and gate it in buildPicked/canAdd.
      // A single parent can have multiple child groups.
      if (hasChildGroups) {
        const childEntries = opt.options;
        // Entries that have their own `options` are groups; otherwise wrap the
        // flat option list into one synthetic group.
        const entriesAreGroups = childEntries.some(c => c && Array.isArray(c.options));
        const childGroupDefs = entriesAreGroups
          ? childEntries
          : [{ name: opt.name, optionType: opt.optionType, options: childEntries }];

        node.childGroups = childGroupDefs.map((cg, ci) => {
          const childKey = `${key}/${id}/g${ci}`;
          const built = walk(cg, childKey);
          built.parentKey = key;
          built.parentId = id;
          return built;
        });
      }

      g.options.push(node);
    });

    groups.push(g);
    map[key] = g;
    return g;
  };

  sections.forEach((s, i) => walk(s, `root/${i}`));
  return { groups, map, flat, idToGroup };
}

// ============================================================================
// QUANTITY STEPPER COMPONENT
// ============================================================================

function QtyStepper({ value, onInc, onDec, disabledInc, disabledDec }) {
  return (
    <div className="qty-stepper">
      <button 
        type="button" 
        onClick={onDec} 
        disabled={disabledDec}
        className="qty-stepper__btn"
      >
        −
      </button>
      <div className="qty-stepper__value">{value || 0}</div>
      <button 
        type="button" 
        onClick={onInc} 
        disabled={disabledInc}
        className="qty-stepper__btn"
      >
        +
      </button>
    </div>
  );
}

// ============================================================================
// WEIGHT STEPPER (for items with useWeight = 1)
// ============================================================================

// Weight is selected in 1/4 lb increments. Default 0.0; an item cannot be
// added until the weight has been increased above 0.
const WEIGHT_STEP = 0.25;
const roundWeight = (w) => Math.round(w * 100) / 100;

function WeightStepper({ weight, onInc, onDec }) {
  return (
    <div className="weight-stepper">
      <span className="weight-stepper__label">Weight</span>
      <div className="weight-stepper__controls">
        <button
          type="button"
          onClick={onDec}
          disabled={weight <= 0}
          className="qty-stepper__btn"
        >
          −
        </button>
        <div className="weight-stepper__value">{roundWeight(weight).toFixed(2)} lb</div>
        <button
          type="button"
          onClick={onInc}
          className="qty-stepper__btn"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// OPTION GROUP COMPONENT (for standard modifiers)
// ============================================================================

function OptionGroup({ model, selections, setSelections, flat }) {
  const { 
    key, name, min, max, included, subLimit, groupType, 
    toppingMatch, isRadio, showQtyStepper, options, required, optionType
  } = model;
  
  const counts = selections[key] || {};
  const totalSelected = Object.values(counts).reduce((a, b) => a + (b || 0), 0);

  // Calculate allowed match type from defaults
  const defaults = options.filter(o => o.default);
  const allowedMatchType = defaults.length 
    ? Math.max(...defaults.map(o => toNum(o.matchType || 0))) 
    : Infinity;

  // Calculate substitutions used
  const subsUsed = Object.entries(counts).reduce((sum, [id, q]) => {
    const m = flat[id];
    if (!m) return sum;
    return sum + (m.default ? 0 : (q || 0));
  }, 0);

  // Toggle selection on/off (for checkbox-style)
  const toggle = (id, matchType = 0, isDefault = false) => {
    setSelections(s => {
      const current = s[key] || {};
      const isCurrentlySelected = (current[id] || 0) > 0;
      
      if (isCurrentlySelected) {
        // Deselect
        const next = { ...current };
        delete next[id];
        return { ...s, [key]: next };
      } else {
        // Select - check if we can
        const total = Object.values(current).reduce((a, b) => a + (b || 0), 0);
        
        // Check max selections (999 means unlimited)
        if (max < 999 && total >= max) return s;
        
        const freeLeft = Math.max(included - total, 0);
        
        // Check topping grade restriction
        if (groupType === 'toppings' && toppingMatch === 'less' && freeLeft > 0) {
          if (matchType > allowedMatchType) return s;
        }
        
        // Check sub limit
        if (!isDefault && subLimit > 0 && subsUsed >= subLimit) {
          return s;
        }
        
        return { ...s, [key]: { ...current, [id]: 1 } };
      }
    });
  };

  // Single selection (radio-style) - clears previous selection
  const setSingle = (id, matchType = 0, isDefault = false) => {
    const freeLeft = Math.max(included - totalSelected, 0);
    
    if (groupType === 'toppings' && toppingMatch === 'less' && freeLeft > 0) {
      if (matchType > allowedMatchType) return;
    }
    
    if (!isDefault && subLimit > 0 && subsUsed >= subLimit) return;
    
    setSelections(s => ({ ...s, [key]: { [id]: 1 } }));
  };

  // Increment qty (for multiSelect with qty steppers)
  const incQty = (id, optMax = 999, matchType = 0, isDefault = false) => {
    setSelections(s => {
      const current = s[key] || {};
      const currentQty = current[id] || 0;
      const total = Object.values(current).reduce((a, b) => a + (b || 0), 0);
      
      // Check max selections
      if (max < 999 && total >= max) return s;
      
      // Check option-level max
      if (currentQty >= optMax) return s;
      
      const freeLeft = Math.max(included - total, 0);
      
      // Check topping grade restriction
      if (groupType === 'toppings' && toppingMatch === 'less' && freeLeft > 0) {
        if (matchType > allowedMatchType) return s;
      }
      
      // Check sub limit
      if (!isDefault && subLimit > 0 && subsUsed >= subLimit && currentQty === 0) {
        return s;
      }
      
      return { ...s, [key]: { ...current, [id]: currentQty + 1 } };
    });
  };

  // Decrement qty
  const decQty = (id) => {
    setSelections(s => {
      const current = s[key] || {};
      const currentQty = current[id] || 0;
      if (currentQty <= 0) return s;
      
      const next = { ...current, [id]: currentQty - 1 };
      if (next[id] === 0) delete next[id];
      return { ...s, [key]: next };
    });
  };

  // Determine satisfaction
  const isSatisfied = totalSelected >= min && (max >= 999 || totalSelected <= max);

  // Build hint text
  let hintText = '';
  if (min > 0 && max < 999 && min === max) {
    hintText = `Choose ${min}`;
  } else if (min > 0 && max < 999) {
    hintText = `Choose ${min}–${max}`;
  } else if (min > 0) {
    hintText = `Choose at least ${min}`;
  } else if (max < 999) {
    hintText = `Up to ${max}`;
  }
  if (included > 0) hintText += ` • First ${included} included`;
  if (subLimit > 0) hintText += ` • Sub limit ${subLimit}`;

  return (
    <div className={`modifier-group ${required ? 'modifier-group--required' : ''} ${isSatisfied && required ? 'modifier-group--satisfied' : ''}`}>
      <div className="modifier-group__header">
        <h3 className="modifier-group__title">{name}</h3>
        <div className="modifier-group__badges">
          {required && (
            <span className={`modifier-group__required ${isSatisfied ? 'modifier-group__required--met' : ''}`}>
              {isSatisfied ? '✓ Required' : 'Required'}
            </span>
          )}
          {hintText && <span className="modifier-group__hint">{hintText}</span>}
        </div>
      </div>

      <div className="modifier-group__options">
        {options.map(opt => {
          const qty = counts[opt.id] || 0;
          const selected = qty > 0;
          const freeLeft = Math.max(included - totalSelected, 0);
          
          // Check if selection would be blocked
          let blocked = false;
          if (!selected) {
            // Check max
            if (max < 999 && totalSelected >= max) blocked = true;
            
            // Check topping grade
            if (groupType === 'toppings' && toppingMatch === 'less' && freeLeft > 0) {
              if (toNum(opt.matchType || 0) > allowedMatchType) blocked = true;
            }
            
            // Check sub limit
            if (!opt.default && subLimit > 0 && subsUsed >= subLimit) blocked = true;
          }

          // For radio buttons, never block unselected options (you're replacing, not adding)
          const showAsBlocked = blocked && !isRadio;

          return (
            <div key={opt.id} className="modifier-option-wrapper">
              <label className={`modifier-option ${selected ? 'modifier-option--selected' : ''} ${showAsBlocked ? 'modifier-option--disabled' : ''}`}>
                <input
                  type={isRadio ? 'radio' : 'checkbox'}
                  name={`modifier_group_${key}`}
                  checked={selected}
                  onChange={() => {
                    if (showAsBlocked && !selected) return; // Don't allow if blocked
                    
                    if (isRadio) {
                      // Radio buttons always allow switching
                      setSingle(opt.id, toNum(opt.matchType || 0), !!opt.default);
                    } else if (showQtyStepper) {
                      // For qty stepper mode, toggle adds 1 or removes all
                      if (selected) {
                        setSelections(s => {
                          const next = { ...s[key] };
                          delete next[opt.id];
                          return { ...s, [key]: next };
                        });
                      } else {
                        incQty(opt.id, opt.optMax, toNum(opt.matchType || 0), !!opt.default);
                      }
                    } else {
                      toggle(opt.id, toNum(opt.matchType || 0), !!opt.default);
                    }
                  }}
                  disabled={showAsBlocked && !selected}
                />
                <span className={`modifier-option__check ${!isRadio ? 'modifier-option__check--checkbox' : ''}`}>
                  {selected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </span>
                <span className="modifier-option__content" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span className="modifier-option__name">{opt.name}</span>
                    {(optionType === 5 || optionType === 6)
                      ? (toNum(opt.price) !== 0 && (
                          <span className="modifier-option__price">+{toNum(opt.price)}%</span>
                        ))
                      : (toNum(opt.price) !== 0 && (
                          <span className="modifier-option__price">+${toNum(opt.price).toFixed(2)}</span>
                        ))}
                  </span>
                  {opt.description && (
                    <span
                      className="modifier-option__description"
                      style={{ fontSize: '0.85rem', lineHeight: 1.4, opacity: 0.75 }}
                    >
                      {opt.description}
                    </span>
                  )}
                </span>
              </label>
              
              {/* Qty stepper for multiSelect mode */}
              {showQtyStepper && selected && (
                <div className="modifier-option__qty">
                  <button 
                    type="button"
                    className="modifier-option__qty-btn"
                    onClick={() => decQty(opt.id)}
                  >−</button>
                  <span className="modifier-option__qty-value">{qty}</span>
                  <button 
                    type="button"
                    className="modifier-option__qty-btn"
                    onClick={() => incQty(opt.id, opt.optMax, toNum(opt.matchType || 0), !!opt.default)}
                    disabled={max < 999 && totalSelected >= max}
                  >+</button>
                </div>
              )}
              
              {/* Nested child groups — revealed only once the parent is selected */}
              {opt.isParent && selected && (opt.childGroups || []).map((cg) => (
                <OptionGroup 
                  key={cg.key}
                  model={cg} 
                  selections={selections} 
                  setSelections={setSelections} 
                  flat={flat}
                />
              ))}
            </div>
          );
        })}
      </div>

      {min > 0 && totalSelected < min && (
        <div className="modifier-group__error">
          Please choose {min === max ? `exactly ${min}` : `at least ${min}`}.
        </div>
      )}
      {max < 999 && totalSelected > max && (
        <div className="modifier-group__error">You can choose up to {max}.</div>
      )}
    </div>
  );
}

// ============================================================================
// PIZZA BUILDER COMPONENT
// ============================================================================

function PizzaBuilder({ item, initialMeta, onConfirm, onClose }) {
  const config = getPizzaConfig(item);
  
  const [sizeIndex, setSizeIndex] = useState(initialMeta?.sizeIndex ?? 0);
  const [qty, setQty] = useState(initialMeta?.qty ?? 1);
  const [toppings, setToppings] = useState({});

  // Initialize toppings from config and initialMeta
  useEffect(() => {
    if (!config) return;
    
    const initial = {};
    
    // Set up default toppings from config
    (config.toppings || []).forEach(grp => {
      const side = (grp.name || 'WHOLE').toUpperCase();
      (grp.options || []).forEach(opt => {
        if (opt.default) {
          initial[String(opt.id)] = {
            id: opt.id,
            name: opt.name,
            side,
            state: 'default',
            activeOn: opt.activeOn || [],
            default: true
          };
        }
      });
    });
    
    // Override with initial meta if editing
    if (initialMeta?.toppings?.length) {
      initialMeta.toppings.forEach(t => {
        initial[String(t.id)] = { ...t };
      });
    }
    
    setToppings(initial);
    
    if (typeof initialMeta?.sizeIndex === 'number') setSizeIndex(initialMeta.sizeIndex);
    if (typeof initialMeta?.qty === 'number') setQty(initialMeta.qty);
  }, [config, initialMeta]);

  if (!config) return null;

  const sizes = config.size || config.sizes || [];
  const sideGroups = (config.toppings || []).reduce((acc, grp) => {
    acc[(grp.name || 'WHOLE').toUpperCase()] = grp.options || [];
    return acc;
  }, {});
  const wholeOptions = sideGroups['WHOLE'] || [];

  const basePrice = toNum(sizes?.[sizeIndex]?.priceBaseTotal ?? item.item_price ?? 0);

  // Toggle topping on/off
  const toggleTopping = (opt) => {
    setToppings(s => {
      const cur = s[String(opt.id)];
      if (cur) {
        // Toggle state
        const newState = cur.default
          ? (cur.state === 'no' ? 'default' : 'no')
          : (cur.state === 'no' ? 'add' : 'no');
        return { ...s, [opt.id]: { ...cur, state: newState } };
      }
      // Add new topping
      return { 
        ...s, 
        [opt.id]: { 
          id: opt.id, 
          name: opt.name, 
          side: 'WHOLE', 
          state: 'add', 
          activeOn: opt.activeOn || [], 
          default: !!opt.default 
        } 
      };
    });
  };

  // Set topping side (LEFT/WHOLE/RIGHT)
  const setSide = (opt, side) => {
    setToppings(s => ({
      ...s,
      [opt.id]: {
        ...(s[opt.id] || { 
          id: opt.id, 
          name: opt.name, 
          side: 'WHOLE', 
          state: opt.default ? 'default' : 'add', 
          activeOn: opt.activeOn || [], 
          default: !!opt.default 
        }),
        side
      }
    }));
  };

  // Toggle extra
  const setExtra = (opt, on) => {
    setToppings(s => {
      const cur = s[opt.id] || { 
        id: opt.id, 
        name: opt.name, 
        side: 'WHOLE', 
        state: 'add', 
        activeOn: opt.activeOn || [], 
        default: !!opt.default 
      };
      const nextState = on ? 'extra' : (cur.default ? 'default' : 'add');
      return { ...s, [opt.id]: { ...cur, state: nextState } };
    });
  };

  // Build topping list with current state
  const toppingList = wholeOptions.map(opt => {
    const cur = toppings[String(opt.id)];
    const effective = cur || (opt.default ? { 
      id: opt.id, 
      name: opt.name, 
      side: 'WHOLE', 
      state: 'default', 
      activeOn: opt.activeOn || [], 
      default: true 
    } : null);
    
    const state = effective?.state;
    const extraOn = state === 'extra';
    const price = effective ? pricePizzaTopping(effective, sizeIndex) : 0;
    const side = effective?.side || 'WHOLE';
    const addPrice = pricePizzaTopping({ 
      id: opt.id, 
      name: opt.name, 
      side, 
      state: 'add', 
      activeOn: opt.activeOn || [] 
    }, sizeIndex);
    const selected = !!effective && state !== 'no';
    
    return { opt, effective, state, extraOn, price, addPrice, side, selected };
  });

  const optionsTotal = toppingList.reduce((s, t) => s + (t.selected ? toNum(t.price) : 0), 0);
  const total = (basePrice + optionsTotal) * qty;

  const handleConfirm = () => {
    const toppingValues = Object.values(toppings);
    const sizeName = sizes?.[sizeIndex]?.sizeName || '';
    onConfirm({ sizeIndex, toppings: toppingValues, basePrice, sizeName, qty });
  };

  return (
    <div className="pizza-builder">
      {/* Size Selection */}
      <div className="pizza-builder__section">
        <h3 className="pizza-builder__section-title">Choose Size</h3>
        <div className="pizza-builder__sizes">
          {sizes.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSizeIndex(i)}
              className={`pizza-builder__size ${i === sizeIndex ? 'pizza-builder__size--selected' : ''}`}
            >
              <span className="pizza-builder__size-name">{s.sizeName}</span>
              <span className="pizza-builder__size-price">${money(toNum(s.priceBaseTotal))}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Toppings */}
      <div className="pizza-builder__section">
        <h3 className="pizza-builder__section-title">Toppings</h3>
        <div className="pizza-builder__toppings">
          {toppingList.map(({ opt, effective, state, extraOn, price, addPrice, side, selected }) => {
            const img = opt?.styleOptions?.imageUrl || opt?.image_url;
            
            return (
              <div key={opt.id} className={`pizza-topping ${selected ? 'pizza-topping--selected' : ''}`}>
                <div className="pizza-topping__main">
                  <div className="pizza-topping__info">
                    {img && (
                      <div className="pizza-topping__image">
                        <img src={img} alt={opt.name} />
                      </div>
                    )}
                    <div className="pizza-topping__details">
                      <div className="pizza-topping__name">{opt.name}</div>
                      <div className="pizza-topping__price">
                        {selected ? (
                          price > 0 ? `+ $${money(price)}` : 
                          effective?.default ? 'Included' : 
                          addPrice > 0 ? `+ $${money(addPrice)}` : 'No extra charge'
                        ) : (
                          addPrice > 0 ? `+ $${money(addPrice)}` : 'No extra charge'
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => toggleTopping(opt)}
                    className={`pizza-topping__toggle ${selected ? 'pizza-topping__toggle--on' : ''}`}
                  >
                    {selected ? '✓' : '+'}
                  </button>
                </div>

                {/* Side selector and Extra toggle */}
                <div className="pizza-topping__controls">
                  <div className="pizza-topping__sides">
                    {['LEFT', 'WHOLE', 'RIGHT'].map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSide(opt, s)}
                        className={`pizza-topping__side ${(effective?.side || 'WHOLE') === s ? 'pizza-topping__side--selected' : ''}`}
                      >
                        {s === 'LEFT' ? 'L' : s === 'RIGHT' ? 'R' : 'W'}
                      </button>
                    ))}
                  </div>
                  
                  <label className="pizza-topping__extra">
                    <input
                      type="checkbox"
                      checked={extraOn}
                      onChange={(e) => setExtra(opt, e.target.checked)}
                    />
                    <span>Extra</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="pizza-builder__footer">
        <div className="pizza-builder__qty">
          <button 
            type="button"
            onClick={() => setQty(q => Math.max(1, q - 1))}
            className="pizza-builder__qty-btn"
          >
            −
          </button>
          <span className="pizza-builder__qty-value">{qty}</span>
          <button 
            type="button"
            onClick={() => setQty(q => q + 1)}
            className="pizza-builder__qty-btn"
          >
            +
          </button>
        </div>
        
        <div className="pizza-builder__total">${money(total)}</div>
        
        <button 
          type="button"
          onClick={handleConfirm}
          className="pizza-builder__add-btn"
        >
          Add to Order
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// STANDARD OPTIONS PICKER COMPONENT
// ============================================================================

function StandardOptionsPicker({ item, modGroups = [], initialPicked = [], initialQty = 1, onConfirm, useWeight = false, weight = 0, onIncWeight, onDecWeight }) {
  const [selections, setSelections] = useState({});
  const [flat, setFlat] = useState({});
  const [models, setModels] = useState([]);
  const [groupMap, setGroupMap] = useState({});
  const [qty, setQty] = useState(initialQty || 1);

  useEffect(() => {
    if (!item) return;
    
    const secs = getOptionSections(item, modGroups);
    const norm = normalizeTree(secs);
    
    setModels(norm.groups.filter(g => g.key.startsWith('root/') && g.parentKey == null));
    setGroupMap(norm.map);
    setFlat(norm.flat);

    // Prefill from initialPicked (when editing)
    const pre = {};
    (initialPicked || []).forEach(p => {
      const id = String(p.id);
      const gid = norm.idToGroup[id];
      if (!gid) return;
      pre[gid] = pre[gid] || {};
      pre[gid][id] = (pre[gid][id] || 0) + 1;

      // Re-select ancestor parent options so nested groups re-open and the parent
      // highlights on edit. A showParent:false parent (e.g. "Whip Cream") isn't
      // saved in the order, so we infer it from the presence of its child.
      let grp = norm.map[gid];
      while (grp && grp.parentKey != null && grp.parentId != null) {
        const pKey = grp.parentKey;
        const pId = String(grp.parentId);
        pre[pKey] = pre[pKey] || {};
        if (!pre[pKey][pId]) pre[pKey][pId] = 1;
        grp = norm.map[pKey];
      }
    });
    
    // Also prefill defaults
    norm.groups.forEach(g => {
      g.options.forEach(opt => {
        if (opt.default && !pre[g.key]?.[opt.id]) {
          pre[g.key] = pre[g.key] || {};
          pre[g.key][opt.id] = 1;
        }
      });
    });
    
    setSelections(pre);
    // Preserve the cart line's quantity when editing.
    setQty(initialQty || 1);
  }, [item, modGroups, initialPicked, initialQty]);

  // Check if all required groups are satisfied
  const canAdd = useMemo(() => {
    // Validate every group, top-level AND nested. A required child group only
    // applies once its parent option is selected.
    for (const model of Object.values(groupMap)) {
      if (model.parentKey != null && model.parentId != null) {
        const parentSel = (selections[model.parentKey] || {})[model.parentId] || 0;
        if (parentSel <= 0) continue;
      }
      const counts = selections[model.key] || {};
      const totalSelected = Object.values(counts).reduce((a, b) => a + (b || 0), 0);
      if (model.min > 0 && totalSelected < model.min) {
        return false;
      }
    }
    return true;
  }, [groupMap, selections]);

  // Build picked options for submission, in MENU ORDER (depth-first over the
  // model tree). Each group emits its selected options in order; a selected
  // parent then recurses into its child groups so nested children land directly
  // after their parent's position (e.g. Lite Whip before Salad, Ranch after).
  const buildPicked = () => {
    const out = [];

    const emitGroup = (group) => {
      const counts = selections[group.key] || {};
      const groupOptionType = toNum(group.optionType || 0);
      const isSurcharge = groupOptionType === 5 || groupOptionType === 6;
      let freeLeft = group.included || 0;

      (group.options || []).forEach(opt => {
        const qtySelected = counts[opt.id] || 0;
        if (qtySelected <= 0) return;

        // A parent option (one with child groups) is added to the order BY
        // DEFAULT. It's only suppressed when showParent is explicitly false
        // (in which case its selected children stand in for it).
        const hiddenParent = opt.isParent && opt.showParent === false;
        if (!hiddenParent) {
          // Lean order modifier — only the fields the POS/kitchen uses. No menu
          // catalog (options/mapOffer/offerList/nestedOptionList/etc).
          const leanMod = (charge, extra) => Object.assign({
            id: opt.id,
            sku: opt.sku || String(opt.id),
            name: opt.name,
            price: charge,
            matchType: opt.matchType || 0,
            printerid: opt.printerid || '',
            description: opt.description || '',
            displayOrder: opt.displayOrder || 0,
            kitchenPrintName: opt.kitchenPrintName || '',
            showParent: opt.showParent === true,
            optionType: groupOptionType
          }, extra || {});

          for (let i = 0; i < qtySelected; i++) {
            if (isSurcharge) {
              // optionType 5/6: price is a PERCENT, carried through for the total
              // calc; price 0 so it's never summed as a flat dollar add.
              out.push(leanMod(0, { percent: toNum(opt.price) }));
            } else {
              const charge = freeLeft > 0 ? 0 : opt.price;
              out.push(leanMod(charge));
              if (freeLeft > 0) freeLeft--;
            }
          }
        }

        // Recurse into nested child groups (only reached because the parent is
        // selected), in menu order — this also prevents orphaned children.
        if (opt.isParent && Array.isArray(opt.childGroups)) {
          opt.childGroups.forEach(emitGroup);
        }
      });
    };

    (models || []).forEach(emitGroup);
    return out;
  };

  // Calculate total via shared helper (handles optionType 5/6 surcharges).
  // For weighted items the effective base is (per-lb price x weight) and qty is
  // forced to 1; passing the weighted base through computeLinePricing keeps the
  // percentage-surcharge math (optionType 6) correct against the real line base.
  const picked = buildPicked();
  const basePrice = toNum(item?.item_price);
  const effectiveBase = useWeight ? roundWeight(basePrice * weight) : basePrice;
  const effectiveQty = useWeight ? 1 : qty;
  const { total } = (() => {
    const r = computeLinePricing(effectiveBase, picked, effectiveQty);
    return { total: r.lineTotal };
  })();

  // Weighted items cannot be added at 0 lb.
  const weightOk = !useWeight || weight > 0;

  return (
    <div className="options-picker">
      {models.length === 0 ? (
        <div className="options-picker__empty">No customizations available</div>
      ) : (
        models.map(m => (
          <OptionGroup
            key={m.key}
            model={m}
            selections={selections}
            setSelections={setSelections}
            flat={flat}
          />
        ))
      )}

      {/* Quantity (or weight for weighed items) */}
      <div className="options-picker__footer">
        {useWeight ? (
          <WeightStepper weight={weight} onInc={onIncWeight} onDec={onDecWeight} />
        ) : (
          <div className="options-picker__qty">
            <button 
              type="button"
              onClick={() => setQty(q => Math.max(1, q - 1))}
              disabled={qty <= 1}
            >
              −
            </button>
            <span>{qty}</span>
            <button 
              type="button"
              onClick={() => setQty(q => q + 1)}
            >
              +
            </button>
          </div>
        )}
        
        <button
          type="button"
          className="options-picker__add-btn"
          onClick={() => onConfirm(picked, effectiveQty)}
          disabled={!canAdd || !weightOk}
        >
          {useWeight && !weightOk ? 'Select a weight' : (total > 0 ? `Add to Order • $${money(total)}` : 'Add to Order')}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN ITEM MODAL COMPONENT
// ============================================================================

export function ItemModal({ open, item, editMeta, modGroups = [], onClose, onAdd }) {
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [weight, setWeight] = useState(0);

  const useWeight = Number(item?.useWeight) === 1;

  // Reset when item changes
  useEffect(() => {
    if (open && item) {
      setSpecialInstructions(editMeta?.specialInstructions || '');
      setWeight(typeof editMeta?.weight === 'number' ? editMeta.weight : 0);
    }
  }, [open, item?.itemid, editMeta]);

  const incWeight = useCallback(() => setWeight(w => roundWeight(w + WEIGHT_STEP)), []);
  const decWeight = useCallback(() => setWeight(w => Math.max(0, roundWeight(w - WEIGHT_STEP))), []);

  if (!open || !item) return null;

  const isPizza = isPizzaItem(item);
  const optionSections = getOptionSections(item, modGroups);
  const hasOptions = optionSections.some(s => s.options?.length > 0);

  // Handle pizza confirmation
  const handlePizzaConfirm = ({ sizeIndex, toppings, basePrice, sizeName, qty }) => {
    const options = toppings.map(t => makePizzaOption(t, sizeIndex)).filter(Boolean);
    const optionsTotal = options.reduce((s, o) => s + toNum(o.price), 0);
    const baseName = (item.item_name || '').replace(/^(\d+\s*inch\s*)/i, '').trim() || item.item_name;
    const lineTotal = (toNum(basePrice) + optionsTotal) * qty;

    const cartItem = {
      id: uid(),
      itemid: item.itemid,
      quantity: qty,
      name: `${sizeName} ${baseName}`.trim(),
      price: basePrice,
      lineTotal,
      modifiers: options,
      modifierTotal: optionsTotal,
      specialInstructions,
      taxrate: item.taxrate || 0.0825,
      _editMeta: {
        kind: 'pizza',
        sizeIndex,
        sizeName,
        basePrice,
        toppings,
        item,
        qty
      }
    };

    onAdd(cartItem);
  };

  // Handle standard options confirmation
  const handleOptionsConfirm = (picked, qty) => {
    const basePrice = toNum(item.item_price);
    // For weighted items: origin (base) = per-lb price x weight, qty forced to 1.
    const effectiveBase = useWeight ? roundWeight(basePrice * weight) : basePrice;
    const effectiveQty = useWeight ? 1 : qty;
    const { modifierTotal, lineTotal } = computeLinePricing(effectiveBase, picked, effectiveQty);
    const name = useWeight ? `${item.item_name} ${roundWeight(weight).toFixed(2)}lb` : item.item_name;

    const cartItem = {
      id: uid(),
      itemid: item.itemid,
      quantity: effectiveQty,
      name,
      price: effectiveBase,
      lineTotal,
      modifiers: picked,
      modifierTotal,
      specialInstructions,
      ...(useWeight ? { weight: roundWeight(weight) } : {}),
      taxrate: item.taxrate || 0.0825,
      _editMeta: {
        kind: 'entree',
        item,
        qty: effectiveQty,
        pickedOptions: picked,
        ...(useWeight ? { weight: roundWeight(weight) } : {})
      }
    };

    onAdd(cartItem);
  };

  // Handle simple item (no modifiers)
  const handleSimpleAdd = (qty = 1) => {
    const basePrice = toNum(item.item_price);
    const effectiveBase = useWeight ? roundWeight(basePrice * weight) : basePrice;
    const effectiveQty = useWeight ? 1 : qty;
    const lineTotal = effectiveBase * effectiveQty;
    const name = useWeight ? `${item.item_name} ${roundWeight(weight).toFixed(2)}lb` : item.item_name;

    const cartItem = {
      id: uid(),
      itemid: item.itemid,
      quantity: effectiveQty,
      name,
      price: effectiveBase,
      lineTotal,
      modifiers: [],
      modifierTotal: 0,
      specialInstructions,
      ...(useWeight ? { weight: roundWeight(weight) } : {}),
      taxrate: item.taxrate || 0.0825,
      _editMeta: {
        kind: 'simple',
        item,
        qty: effectiveQty,
        ...(useWeight ? { weight: roundWeight(weight) } : {})
      }
    };

    onAdd(cartItem);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal item-modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '680px', width: '92%' }}
      >
        <button className="modal__close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Item Header */}
        <div className="item-modal__header">
          {item.image_url && (
            <div className="item-modal__image">
              <img src={item.image_url} alt={item.item_name} />
            </div>
          )}
          <div className="item-modal__info">
            <h2 className="item-modal__name">{item.item_name}</h2>
            {(() => {
              // Full details in the modal: prefer the long description; fall back
              // to a short description if that's all the item has.
              const full = (item.description || item.item_desc
                || item.short_description || item.shortDescription
                || item.item_short_description || item.short_desc || item.descriptionShort || '').trim();
              return full ? <p className="item-modal__desc">{full}</p> : null;
            })()}
            {!isPizza && parseFloat(item.item_price) > 0 && (
              <p className="item-modal__price">${parseFloat(item.item_price).toFixed(2)}{useWeight ? ' / lb' : ''}</p>
            )}
          </div>
        </div>

        {/* Content - Pizza Builder or Standard Options */}
        <div className="item-modal__body">
          {isPizza ? (
            <PizzaBuilder
              item={item}
              initialMeta={editMeta}
              onConfirm={handlePizzaConfirm}
              onClose={onClose}
            />
          ) : hasOptions ? (
            <StandardOptionsPicker
              item={item}
              modGroups={modGroups}
              initialPicked={editMeta?.pickedOptions || []}
              initialQty={editMeta?.qty || 1}
              onConfirm={handleOptionsConfirm}
              useWeight={useWeight}
              weight={weight}
              onIncWeight={incWeight}
              onDecWeight={decWeight}
            />
          ) : (
            <SimpleItemPicker
              item={item}
              onConfirm={handleSimpleAdd}
              initialQty={editMeta?.qty || 1}
              useWeight={useWeight}
              weight={weight}
              onIncWeight={incWeight}
              onDecWeight={decWeight}
            />
          )}

          {/* Special Instructions - only show for non-pizza (pizza has it in footer) */}
          {!isPizza && (
            <div className="item-modal__instructions">
              <label className="item-modal__instructions-label">
                Special Instructions
              </label>
              <textarea
                className="item-modal__instructions-input"
                placeholder="Any special requests? (allergies, modifications, etc.)"
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// SIMPLE ITEM PICKER (no modifiers)
// ============================================================================

function SimpleItemPicker({ item, onConfirm, initialQty = 1, useWeight = false, weight = 0, onIncWeight, onDecWeight }) {
  const [qty, setQty] = useState(initialQty || 1);
  useEffect(() => { setQty(initialQty || 1); }, [item, initialQty]);
  const price = toNum(item.item_price);
  const effectiveQty = useWeight ? 1 : qty;
  const total = useWeight ? roundWeight(price * weight) : price * qty;
  const weightOk = !useWeight || weight > 0;

  return (
    <div className="simple-picker">
      <div className="simple-picker__footer">
        {useWeight ? (
          <WeightStepper weight={weight} onInc={onIncWeight} onDec={onDecWeight} />
        ) : (
          <div className="simple-picker__qty">
            <button 
              type="button"
              onClick={() => setQty(q => Math.max(1, q - 1))}
              disabled={qty <= 1}
            >
              −
            </button>
            <span>{qty}</span>
            <button 
              type="button"
              onClick={() => setQty(q => q + 1)}
            >
              +
            </button>
          </div>
        )}
        
        <button
          type="button"
          className="simple-picker__add-btn"
          onClick={() => onConfirm(effectiveQty)}
          disabled={!weightOk}
        >
          {useWeight && !weightOk ? 'Select a weight' : (total > 0 ? `Add to Order • $${money(total)}` : 'Add to Order')}
        </button>
      </div>
    </div>
  );
}

export default ItemModal;